/* eslint-disable camelcase */
import { Dispatch, SetStateAction, useCallback, useState, useLayoutEffect, forwardRef, Fragment } from 'react'
import { Box, Typography, TableHead, TableRow, TableCell, TableBody } from '@mui/material'
import dayjs from 'dayjs'
import theme from 'theme'

/* Utils */
import axios from '../../../../../lib/axios'
import { Pagination } from '../../useCoverageAnalyzerData'
import {
  AcaComparisonPayloadSnowflake,
  AcaCoverageFilter,
  AcaLastSeen,
  AcaOperatingSystem,
  ALL_SOURCES_SENTINEL,
  AnalysisDataTable,
  IntegrationInstance,
  LastSeenWithin,
  MatchingRecord,
  OperatingSystem,
  TableHeaders
} from '../../../../../models'
import { camelToSnake, escapeRegExp, getInstanceCircleLogo } from 'lib/utils'

/* Components */
import { TableContainerW, TableWrapper } from './DataTable.styles'
import TableColumnArrow from '../../../../components/svg/coverage-analyzer/TableColumnArrow'
import { IconTooltip } from 'ui/pages/security-stack-map/components/IconTooltip'
import LoadingRows from '../LoadingRows'
import { getCompareToAllCoverage, mapAnalysisData, mapClusterData } from '../../utils'

type Ref = HTMLDivElement | null

interface Props {
  columns: TableHeaders[]
  visibleColumns: TableHeaders[]
  setColumns: Dispatch<SetStateAction<TableHeaders[]>>
  setOpenRowDrawer: Dispatch<SetStateAction<boolean>>
  setActiveRowItem: Dispatch<SetStateAction<AnalysisDataTable | null>>
  firstInstance: IntegrationInstance | undefined
  secondInstance: IntegrationInstance | undefined
  pagination: Pagination
  setPagination: Dispatch<SetStateAction<Pagination>>
  selectedSeenWithin: LastSeenWithin
  selectedOperatingSystem: OperatingSystem
  searchTerm: string
  handleSort: ({
    column,
    direction,
    integrationIds
  }: {
    column: string
    direction: string
    integrationIds: string[]
  }) => Promise<void>
  rows: AnalysisDataTable[]
  setRows: Dispatch<SetStateAction<AnalysisDataTable[]>>
  fetchTotalAssets: ({
    integrationIds,
    lastSeenWithin
  }: {
    integrationIds: string[]
    lastSeenWithin: AcaLastSeen
  }) => Promise<void>
  firstSourceInField: string
  secondSourceInField: string
  setFirstSource: Dispatch<SetStateAction<string>>
  setSecondSource: Dispatch<SetStateAction<string>>
  selectedInstances: IntegrationInstance[]
  search: string
  tableFilters: boolean[]
  isCompareToAll?: boolean
  instances: IntegrationInstance[]
}

const DataTable = forwardRef<Ref, Props>(
  (
    {
      columns,
      visibleColumns,
      setColumns,
      setOpenRowDrawer,
      setActiveRowItem,
      firstInstance,
      secondInstance,
      pagination,
      selectedSeenWithin,
      selectedOperatingSystem,
      searchTerm,
      setPagination,
      handleSort,
      rows,
      setRows,
      fetchTotalAssets,
      firstSourceInField,
      secondSourceInField,
      setFirstSource,
      setSecondSource,
      selectedInstances,
      search,
      tableFilters,
      isCompareToAll,
      instances
    },
    ref
  ) => {
    const [loading, setLoading] = useState(false)
    const [hasMore, setHasMore] = useState(!!pagination.next_page)
    const [distanceBottom, setDistanceBottom] = useState(0)

    const canShow = (key: string) => {
      const findItem = columns.find((column) => column.dataKey === key)
      return findItem?.show
    }

    const openDrawer = (item: AnalysisDataTable) => {
      setOpenRowDrawer(true)
      setActiveRowItem(item)
    }

    /* Compare-to-All rows cluster several contributing records (matchingRecordsA + matchingRecordsB) and the
     * cell only shows the canonical value. Unlike standard mode the row never carries `second*` fields, so the
     * old `row.field && row.secondField` test never fired and the "+N" badge went missing. These helpers count
     * the additional values held by the cluster's records, matching what the asset-details drawer enumerates. */
    const clusterRecords = (row: AnalysisDataTable): MatchingRecord[] => [
      ...(row.matchingRecordsA || []),
      ...(row.matchingRecordsB || [])
    ]

    /* last_seen uses the 0001-01-01 sentinel for "missing", same as the drawer. */
    const hasLastSeenValue = (value?: string | null): boolean => !!value && !value.includes('0001-01-01')

    /* Total value entries across the cluster for a field (arrays count each non-empty entry, like MAC addresses). */
    const countClusterValues = (row: AnalysisDataTable, field: keyof MatchingRecord): number =>
      clusterRecords(row).reduce((sum, record) => {
        const value = record[field]
        if (Array.isArray(value)) return sum + value.filter(Boolean).length
        if (field === 'last_seen') return sum + (hasLastSeenValue(value as string) ? 1 : 0)
        return sum + (value ? 1 : 0)
      }, 0)

    /* Overflow badge count for a Compare-to-All cell: cluster values minus the one already shown in the cell. */
    const clusterOverflow = (row: AnalysisDataTable, field: keyof MatchingRecord, primaryShown: boolean): number =>
      Math.max(0, countClusterValues(row, field) - (primaryShown ? 1 : 0))

    const sortData = async (index: number) => {
      /* Skip source re-sync in Compare-to-All — the sentinel slot must be preserved and there is no
       * second instance to read. */
      if (!isCompareToAll) {
        const firstSelectedId = selectedInstances[0].id
        const secondSelectedId = selectedInstances[1].id
        if (firstSelectedId !== firstSourceInField) {
          setFirstSource(firstSelectedId)
        }
        if (secondSelectedId !== secondSourceInField) {
          setSecondSource(secondSelectedId)
        }
      }

      const temp = [...columns.map((c) => ({ ...c, sort: false }))]

      temp[index].sortType = temp[index].sortType === 'asc' ? 'desc' : 'asc'
      temp[index].sort = true
      const tempName = camelToSnake(temp[index].dataKey)
      // Map dataKey to API sort field - 'hostName' needs special handling as API expects 'hostname' not 'host_name'
      let column =
        tempName === 'original_serial_number' ? 'serial_number' : tempName === 'last_address' ? 'last_seen' : tempName
      if (tempName === 'host_name') column = 'hostname'
      const direction = temp[index].sortType.toUpperCase()

      setColumns(temp)

      await handleSort({ column, direction, integrationIds: selectedInstances.map((n) => n.id) })
    }

    const loadMore = useCallback(async () => {
      const loadItems = async () => {
        try {
          const sort = columns.find((c) => c.sort)
          const operating_system = selectedOperatingSystem.toLowerCase() as AcaOperatingSystem
          const last_seen = selectedSeenWithin.toLowerCase() as AcaLastSeen
          const search_term = searchTerm
          // Map dataKey to API sort field - 'hostName' needs special handling as API expects 'hostname' not 'host_name'
          const getSortField = (dataKey: string) => (dataKey === 'hostName' ? 'hostname' : camelToSnake(dataKey))
          const sort_by = sort ? getSortField(sort.dataKey) : 'last_seen'

          const payload: any = {
            operating_system,
            integration_ids: [firstInstance?.id || '', secondInstance?.id || ''],
            last_seen,
            page_number: pagination.current_page + 1,
            sort_by,
            search_term
          }
          const payloadSnowflake: AcaComparisonPayloadSnowflake = {
            query_filter: {
              product_instances: [firstInstance?.instance_name || '', secondInstance?.instance_name || ''],
              last_seen,
              operating_system,
              search_term,
              sort_field: sort_by
            },
            limit: 20,
            page_number: pagination.current_page + 1
          }

          if (sort) {
            payload.sort_direction = sort.sortType.toUpperCase()
            payloadSnowflake.query_filter.sort_direction = sort.sortType.toUpperCase()
          }

          const coverageFilterIndex = tableFilters.findIndex((c) => c)

          if (coverageFilterIndex > -1) {
            const coverage =
              coverageFilterIndex === 0
                ? AcaCoverageFilter.FirstNotSecond
                : coverageFilterIndex === 1
                ? AcaCoverageFilter.SecondNotFirst
                : coverageFilterIndex === 2
                ? AcaCoverageFilter.Intersection
                : AcaCoverageFilter.SymmetricDifference
            payload.coverage = coverage
            payloadSnowflake.query_filter.coverage_comparison = coverage
          } else {
            delete payload.coverage
          }

          /* Compare-to-All: paginate the clustering endpoint (picked product = firstInstance). */
          if (isCompareToAll) {
            const picked = firstInstance
            if (!picked) {
              setLoading(false)
              return
            }
            payloadSnowflake.query_filter.product_instances = [picked.instance_name || '', ALL_SOURCES_SENTINEL]

            /* Re-resolve coverage with the A/B swap (sentinel may be in Source A). */
            const ctaCoverage = getCompareToAllCoverage(tableFilters, firstSourceInField === ALL_SOURCES_SENTINEL)
            if (ctaCoverage) {
              payloadSnowflake.query_filter.coverage_comparison = ctaCoverage
            } else {
              delete payloadSnowflake.query_filter.coverage_comparison
            }

            const ctaRes = await axios.post('/api/v4/asset_coverage/compare-to-all/records/', payloadSnowflake)

            if (ctaRes.status.toString().includes('20')) {
              const clusterRows = mapClusterData(ctaRes.data.data)
              setRows([...rows, ...clusterRows])
              setHasMore(!!ctaRes.data.pagination.next_page)
              setPagination({ ...ctaRes.data.pagination, total_records: ctaRes.data.pagination.total_results })
            }
            setLoading(false)
            return
          }

          let res
          const isV2 = Boolean(firstInstance?.is_v2 && secondInstance?.is_v2)

          if (isV2) {
            res = await axios.post('/api/v4/asset_coverage/records', payloadSnowflake)
          } else {
            await fetchTotalAssets({
              integrationIds: [firstInstance?.id || '', secondInstance?.id || ''],
              lastSeenWithin: selectedSeenWithin.toLowerCase() as AcaLastSeen
            })

            res = await axios.post('/api/v3/aca/comparison', payload)
          }

          if (res.status.toString().includes('20')) {
            const d = mapAnalysisData(
              isV2,
              res.data.data,
              firstInstance?.instance_name || '',
              secondInstance?.instance_name || ''
            )

            const pagination = {
              ...res.data.pagination
            }
            if (isV2) {
              pagination.total_records = res.data.pagination.total_results
            }
            setRows([...rows, ...d])
            setHasMore(!!res.data.pagination.next_page)
            setPagination(pagination)
          }
        } catch (err) {
          console.error(err)
        } finally {
          setLoading(false)
        }
      }

      setLoading(true)
      await loadItems()
    }, [rows])

    const handleScroll = useCallback(async () => {
      if (!ref || !(ref as any).current) return

      const bottom = (ref as any).current.scrollHeight - (ref as any).current.clientHeight

      if (!distanceBottom) {
        setDistanceBottom(Math.round(bottom + 10))
      }
      if ((ref as any).current.scrollTop > bottom - distanceBottom && hasMore && !loading) {
        await loadMore()
      }
    }, [loading, loadMore, distanceBottom, hasMore])

    useLayoutEffect(() => {
      const tableContainer = (ref as any).current
      if (tableContainer) {
        tableContainer.addEventListener('scroll', handleScroll)

        return () => tableContainer.removeEventListener('scroll', handleScroll)
      }
    }, [handleScroll])

    const renderSingleInstance = (row: AnalysisDataTable) => {
      if (!firstInstance || !secondInstance) return null

      const isV2 = Boolean(firstInstance.is_v2 && secondInstance.is_v2)
      let result: IntegrationInstance | null = null
      if (isV2) {
        result = firstInstance.instance_name === row.coverage ? firstInstance : secondInstance
      } else {
        const instanceIdPart = row.coverage.slice(-8)
        result = firstInstance.id.includes(instanceIdPart) ? firstInstance : secondInstance
      }

      return (
        <IconTooltip
          PopperProps={{
            sx: {
              marginTop: `-${theme.spacing.sm}px !important`
            }
          }}
          title={
            <>
              <Box className="tooltip-wrapper">
                {result && (
                  <>
                    {result.integration_name && (
                      <Typography className="tooltip-text">{result.integration_name}</Typography>
                    )}
                    <Typography className={result.integration_name ? 'tooltip-subtext' : 'tooltip-text'}>
                      {result.name}
                    </Typography>
                  </>
                )}
              </Box>
            </>
          }
          placement="bottom-start"
        >
          <img src={getInstanceCircleLogo(result)} alt="" width="24px" height="24px" />
        </IconTooltip>
      )
    }

    /* Compare-to-All: render an icon for each contributing instance from covering_instances (slugs),
     * capped with a "+N" overflow. covering_instances is the source of truth per docs/aca-api-spec.md. */
    const COVERAGE_ICONS_SHOWN = 3
    const renderCoveringInstances = (row: AnalysisDataTable) => {
      const slugs = row.coveringInstances || []
      const shown = slugs.slice(0, COVERAGE_ICONS_SHOWN)
      const overflow = slugs.length - shown.length

      return (
        <Box className="coverage-wrapper">
          {shown.map((slug) => {
            const inst = instances.find((n) => n.instance_name === slug)
            return (
              <IconTooltip
                key={slug}
                PopperProps={{ sx: { marginTop: `-${theme.spacing.sm}px !important` } }}
                title={
                  <Box className="tooltip-wrapper">
                    {inst?.integration_name && (
                      <Typography className="tooltip-text">{inst.integration_name}</Typography>
                    )}
                    <Typography className={inst?.integration_name ? 'tooltip-subtext' : 'tooltip-text'}>
                      {inst?.name || slug}
                    </Typography>
                  </Box>
                }
                placement="bottom-start"
              >
                <img src={getInstanceCircleLogo(inst)} alt="" width="24px" height="24px" />
              </IconTooltip>
            )
          })}
          {overflow > 0 && <Typography className="count">+{overflow}</Typography>}
        </Box>
      )
    }

    const highlightMatch = (text: string | string[], visibleChars = 20) => {
      if (!search) return Array.isArray(text) ? text[0] : text

      const textsToMatch = Array.isArray(text) ? text : [text]
      const textToDisplay = Array.isArray(text) ? text[0] : text

      if (!textToDisplay) return textToDisplay

      const hasMatch = textsToMatch.some((t) => t?.toLowerCase().includes(search.toLowerCase()))

      if (!hasMatch) return textToDisplay

      const searchLower = search.toLowerCase()
      const textLower = textToDisplay.toLowerCase()
      const matchIndex = textLower.indexOf(searchLower)

      // Smart truncation: if text is long and match would be hidden, center around the
      let displayText = textToDisplay
      if (textToDisplay.length > visibleChars && matchIndex >= 0) {
        // If match starts near or past the visible area, do smart truncation
        if (matchIndex > visibleChars - search.length - 3) {
          const contextBefore = Math.floor((visibleChars - search.length) / 2)
          const contextAfter = visibleChars - search.length - contextBefore

          const start = Math.max(0, matchIndex - contextBefore)
          const end = Math.min(textToDisplay.length, matchIndex + search.length + contextAfter)

          displayText = textToDisplay.slice(start, end)
          if (start > 0) displayText = '...' + displayText
          if (end < textToDisplay.length) displayText = displayText + '...'
        }
      }

      const regex = new RegExp(`(${escapeRegExp(search)})`, 'gi')
      return displayText.split(regex).map((part, index) =>
        regex.test(part) ? (
          <span key={index} style={{ color: theme.colors.cta, fontWeight: 700 }}>
            {part}
          </span>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        )
      )
    }

    return (
      <Box>
        <TableContainerW ref={ref}>
          <TableWrapper sx={{ minWidth: 650 }} stickyHeader aria-label="sticky table" style={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {columns
                  .filter((item) => item.show)
                  .map((column, index) => {
                    return (
                      <TableCell
                        key={index}
                        className={column.sort ? 'no-padding' : ''}
                        onClick={() => sortData(index)}
                      >
                        <Box className="row-wrapper header-cell-inner">
                          <Typography className="row-header-text">{column.name}</Typography>

                          {column.sort && (
                            <Box
                              className={`${column.sortType === 'desc' ? '' : 'desc-icon'} icon-wrapper`}
                              onClick={() => sortData(index)}
                            >
                              <TableColumnArrow />
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    )
                  })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                if (loading && index > rows.length - 5) {
                  return <LoadingRows height="50px !important" columns={visibleColumns.length} />
                }
                return (
                  <TableRow key={index} onClick={() => openDrawer(row)}>
                    {canShow('coverage') && (
                      <TableCell>
                        {isCompareToAll ? (
                          renderCoveringInstances(row)
                        ) : row.coverage === 'both' ? (
                          <Box className="coverage-wrapper">
                            <IconTooltip
                              PopperProps={{
                                sx: {
                                  marginTop: `-${theme.spacing.sm}px !important`
                                }
                              }}
                              title={
                                <>
                                  <Box className="tooltip-wrapper">
                                    {firstInstance?.integration_name && (
                                      <Typography className="tooltip-text">
                                        {firstInstance?.integration_name}
                                      </Typography>
                                    )}
                                    <Typography
                                      className={firstInstance?.integration_name ? 'tooltip-subtext' : 'tooltip-text'}
                                    >
                                      {firstInstance?.name}
                                    </Typography>
                                  </Box>
                                </>
                              }
                              placement="bottom-start"
                            >
                              <img
                                src={getInstanceCircleLogo(firstInstance)}
                                alt=""
                                width="24px"
                                height="24px"
                              />
                            </IconTooltip>
                            <IconTooltip
                              PopperProps={{
                                sx: {
                                  marginTop: `-${theme.spacing.sm}px !important`
                                }
                              }}
                              title={
                                <>
                                  <Box className="tooltip-wrapper">
                                    {secondInstance?.integration_name && (
                                      <Typography className="tooltip-text">
                                        {secondInstance?.integration_name}
                                      </Typography>
                                    )}
                                    <Typography
                                      className={secondInstance?.integration_name ? 'tooltip-subtext' : 'tooltip-text'}
                                    >
                                      {secondInstance?.name}
                                    </Typography>
                                  </Box>
                                </>
                              }
                              placement="bottom-start"
                            >
                              <img
                                src={getInstanceCircleLogo(secondInstance)}
                                alt=""
                                width="24px"
                                height="24px"
                              />
                            </IconTooltip>
                          </Box>
                        ) : (
                          renderSingleInstance(row)
                        )}
                      </TableCell>
                    )}

                    {canShow('hostName') &&
                      (() => {
                        const overflow = isCompareToAll
                          ? clusterOverflow(row, 'hostname', Boolean(row.hostName))
                          : row.hostName && row.secondHostName
                          ? 1
                          : 0
                        return (
                          <TableCell>
                            <Box className="cell-wrapper">
                              <Typography
                                className={`ellipsis ${!row.hostName && !row.secondHostName && 'empty-cell'}`}
                              >
                                {highlightMatch(row.hostName || row.secondHostName || '━━')}
                              </Typography>
                              {overflow > 0 && (
                                <Typography className="count" onClick={() => openDrawer(row)}>
                                  +{overflow}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        )
                      })()}

                    {canShow('serialNumber') &&
                      (() => {
                        const overflow = isCompareToAll
                          ? clusterOverflow(row, 'serial_number', Boolean(row.serialNumber))
                          : row.serialNumber && row.secondSerialNumber
                          ? 1
                          : 0
                        return (
                          <TableCell>
                            <Box className="cell-wrapper">
                              <Typography
                                className={`ellipsis ${!row.serialNumber && !row.secondSerialNumber && 'empty-cell'}`}
                              >
                                {highlightMatch(row.serialNumber || row.secondSerialNumber || '━━')}
                              </Typography>
                              {overflow > 0 && (
                                <Typography className="count" onClick={() => openDrawer(row)}>
                                  +{overflow}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        )
                      })()}

                    {canShow('macAddress') &&
                      (() => {
                        /* Normalize to non-empty lists. Empty arrays are truthy, which previously let an
                         * empty cell fall through to a blank value and a spurious "+0" badge. */
                        const toList = (value: unknown): string[] =>
                          Array.isArray(value)
                            ? value.filter((v): v is string => Boolean(v))
                            : value
                            ? [String(value)]
                            : []
                        const macList = toList(row.macAddress)
                        const secondMacList = toList(row.secondMacAddress)
                        const primaryMac = macList[0] || secondMacList[0] || ''
                        /* Count of additional addresses beyond the one shown. Compare-to-All sums every address
                         * across the cluster's records; standard mode collapses the second source to +1. */
                        const extra = isCompareToAll
                          ? clusterOverflow(row, 'mac_addresses', Boolean(primaryMac))
                          : macList.length > 0
                          ? macList.length - 1 + (secondMacList.length > 0 ? 1 : 0)
                          : Math.max(0, secondMacList.length - 1)

                        return (
                          <TableCell>
                            <Box className="cell-wrapper">
                              <Typography className={`ellipsis ${!primaryMac && 'empty-cell'}`}>
                                {highlightMatch(primaryMac || '━━')}
                              </Typography>
                              {extra > 0 && (
                                <Typography className="count" onClick={() => openDrawer(row)}>
                                  +{extra}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        )
                      })()}

                    {canShow('lastSeen') &&
                      (() => {
                        const overflow = isCompareToAll
                          ? clusterOverflow(row, 'last_seen', hasLastSeenValue(row.lastSeen))
                          : row.lastSeen && row.secondLastSeen
                          ? 1
                          : 0
                        return (
                          <TableCell>
                            <Box className="cell-wrapper">
                              <Typography
                                className={`ellipsis ${
                                  ((!row.lastSeen && !row.secondLastSeen) || row.lastSeen?.includes('0001-01-01')) &&
                                  'empty-cell'
                                }`}
                              >
                                {row.lastSeen?.includes('0001-01-01')
                                  ? '━━'
                                  : row.lastSeen
                                  ? dayjs(row.lastSeen || '').format('YYYY-MM-DD HH:MM:ss')
                                  : row.secondLastSeen
                                  ? dayjs(row.secondLastSeen || '').format('YYYY-MM-DD HH:MM:ss')
                                  : '━━'}
                              </Typography>
                              {overflow > 0 && (
                                <Typography className="count" onClick={() => openDrawer(row)}>
                                  +{overflow}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        )
                      })()}

                    {canShow('operatingSystem') &&
                      (() => {
                        const overflow = isCompareToAll
                          ? clusterOverflow(row, 'operating_system', Boolean(row.operatingSystem))
                          : row.operatingSystem && row.secondOperatingSystem
                          ? 1
                          : 0
                        return (
                          <TableCell>
                            <Box className="cell-wrapper">
                              <Typography
                                className={`ellipsis ${
                                  !row.operatingSystem && !row.secondOperatingSystem && 'empty-cell'
                                }`}
                              >
                                {row.operatingSystem || row.secondOperatingSystem || '━━'}
                              </Typography>
                              {overflow > 0 && (
                                <Typography className="count" onClick={() => openDrawer(row)}>
                                  +{overflow}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        )
                      })()}

                    {canShow('assetId') &&
                      (() => {
                        const overflow = isCompareToAll
                          ? clusterOverflow(row, 'asset_id', Boolean(row.assetId))
                          : row.assetId && row.secondAssetId
                          ? 1
                          : 0
                        return (
                          <TableCell>
                            <Box className="cell-wrapper">
                              <Typography className="ellipsis">{highlightMatch(row.assetId || '')}</Typography>
                              {overflow > 0 && (
                                <Typography className="count" onClick={() => openDrawer(row)}>
                                  +{overflow}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        )
                      })()}
                  </TableRow>
                )
              })}
            </TableBody>
          </TableWrapper>
        </TableContainerW>
      </Box>
    )
  }
)

export default DataTable
